/**
 * Context for server-side Segment calls.
 * IP and User-Agent are injected server-side from request headers.
 */
interface SegmentServerContext {
    page?: { url?: string; path?: string; referrer?: string; title?: string }
    traits?: { email?: string; phone?: string }
    referrer?: { id: string; type: string }
    library?: { name: string; version: string }
}

/**
 * Track an event via server-side Segment HTTP API.
 * Bypasses ad blockers by sending through our server → Segment's API.
 * Server injects IP and User-Agent from request headers.
 *
 * @returns true if the request succeeded, false otherwise
 */
export const segmentServerTrack = async (
    eventName: string,
    properties?: Record<string, unknown>,
    options?: {
        userId?: string
        anonymousId?: string
        context?: SegmentServerContext
        messageId?: string
    },
): Promise<boolean> => {
    try {
        const response = await fetch('/api/track/segment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'track',
                event: eventName,
                userId: options?.userId,
                anonymousId: options?.anonymousId,
                properties,
                context: {
                    ...options?.context,
                    page: {
                        url: window.location.href,
                        path: window.location.pathname,
                        referrer: document.referrer,
                        title: document.title,
                    },
                    library: {
                        name: 'step-web-server',
                        version: '1.0.0',
                    },
                },
                timestamp: new Date().toISOString(),
                messageId: options?.messageId || crypto.randomUUID(),
            }),
        })
        return response.ok
    } catch {
        return false
    }
}

/**
 * Identify a user via server-side Segment HTTP API.
 * Bypasses ad blockers by sending through our server → Segment's API.
 *
 * @returns true if the request succeeded, false otherwise
 */
export const segmentServerIdentify = async (
    userId?: string,
    traits?: Record<string, unknown>,
    anonymousId?: string,
    context?: SegmentServerContext,
): Promise<boolean> => {
    try {
        const response = await fetch('/api/track/segment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'identify',
                userId,
                anonymousId,
                traits,
                context: {
                    ...context,
                    page: {
                        url: window.location.href,
                        path: window.location.pathname,
                        referrer: document.referrer,
                        title: document.title,
                    },
                    library: {
                        name: 'step-web-server',
                        version: '1.0.0',
                    },
                },
                timestamp: new Date().toISOString(),
                messageId: crypto.randomUUID(),
            }),
        })
        return response.ok
    } catch {
        return false
    }
}
